/*
 * INDEX.JSX - Task List Main Component with Global User Permissions
 *
 *
 * SUMMARY:
 * Main task list component that serves as the primary interface for project task management
 * with enhanced permission controls respecting global user privileges during critical
 * project states and operations.
 *
 */
import idx from "idx";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Routes, Route } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import "../scss/project-management.scss";
import "../scss/task.scss";
import "../scss/table-widths.scss";
import { Paper } from "@material-ui/core";
import {
  updateStage,
  updateTask,
  updateSubTask,
  getOptions,
  updateOption,
  convertTask,
  deleteTask,
  deleteSubTask,
  updateTaskOrderingId,
  duplicateProjectTask,
  deleteStage,
  batchUpdateStageOrderingId,
  changeSection,
  copySection,
  getSubTaskList,
} from "../../../actions/project-management.action";
import { PROJECT_MANAGEMENT } from "../../../constants/route-paths";
import { assignTaskFields } from "../../../constants/fields.constant";
import { updateProjectAction } from "../../../slice/project-detail";
import { clearTaskAction } from "../../../slice/add-task";
import AddTask from "../add-task";
import CompletionPercentageDialog from "../../../components/completion-percentage-dialog";
import DeleteDialog from "../../../components/delete-dialog";
import StageSelectDialog from "../stage-select-dialog";
import AddStage from "../add-stage";
import Modal from "../../../components/modal/modal";
import { handleDragEnd } from "../../../util/reorder";
import { Menu, MenuItem, Grid } from "@mui/material";
import ListTaskTemplate from "../../../components/list-template/task";
import StageAccordionTaskList from "../../../components/stage-accordion/project-task-list";
import { Card, CardBody, CardHeader } from "react-bootstrap";
import { extractLastStageData } from "../../../util/common";
import SectionChangeModal from "../../../components/change-section-dialog";
import { convertToReactSelectFormat } from "../../../util/section-dropdown-format";
import { ValidateField, ValidatorTypes } from "../../../util/validators";
import Tasks from "../../../routes/projects/tasks";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { findStageData } from "../../../components/stage-accordion/data/project-task-list";
import MultiSelect from "../../../components/inputs/multi-select";
import _ from "lodash";
import Button from "react-bootstrap/Button";
import {
  getProjectSummaryData,
} from "../utils/get-project-summary";
import { getFormatedData } from "./task";
import ProfitMarginAdjustment from "../../../components/profit-margin-adjustment/ProfitMarginAdjustment";
import { adjustProfitMargin } from "../../../actions/project-management.action";
import AddVariation from "../add-variation";
import { ProposalType } from "../../../util/enums";
import ProposalDialog from "../proposal-dialog";
import { updateStageAttributionLevel } from "../../../actions/project-management.action";
import { addSectionDataAction, addOptionDataAction } from "../../../slice/project-summary";

/**
 * Recursively updates the tasks array for a specific stage ID within the section tree.
 *
 * @param {Array} stageList - Hierarchical stage tree
 * @param {string} targetStageId - ID of stage to update
 * @param {Array} newTasks - Reordered tasks array
 * @returns {Array} Updated stage tree
 */
const updateStageTasksInTree = (stageList = [], targetStageId, newTasks = []) => {
  return stageList.map((stage) => {
    if (stage.id === targetStageId) {
      return { ...stage, tasks: newTasks };
    }
    if (stage.stages && Array.isArray(stage.stages) && stage.stages.length > 0) {
      return {
        ...stage,
        stages: updateStageTasksInTree(stage.stages, targetStageId, newTasks),
      };
    }
    return stage;
  });
};

/**
 * Recursively updates nested child stages for a specific parent stage ID within the section tree.
 *
 * @param {Array} stageList - Hierarchical stage tree
 * @param {string} parentId - Parent stage ID
 * @param {Array} newChildren - Reordered child stages array
 * @returns {Array} Updated stage tree
 */
const updateNestedStagesInTree = (stageList = [], parentId, newChildren = []) => {
  return stageList.map((stage) => {
    if (stage.id === parentId) {
      return { ...stage, stages: newChildren };
    }
    if (stage.stages && Array.isArray(stage.stages) && stage.stages.length > 0) {
      return {
        ...stage,
        stages: updateNestedStagesInTree(stage.stages, parentId, newChildren),
      };
    }
    return stage;
  });
};

export default function TaskList({
  writePermission,
  updatePermission,
  financePermission,
  ...props
}) {
  const initialFieldOpsConfirmation = {
    open: false,
    entityType: "",
    entityId: "",
    nextValue: false,
    entityName: "",
  };
  const [taskListState, setTaskListState] = useState({
    anchorEl: null,
    convertOptiontoTask: false,
    stageDialogState: false,
    menuItems: {},
    selectedTaskTemplateStage: "",
    showTaskTemplateDialog: false,
    selectedTemplateTask: null,

    open: false,
    customstyle: "",
    taskCost: [],
    stagesCost: [],
    quoteType: "",
    variations: [],
    proposal: {},
    userNames: [],
    taskData: {},
    totalVariationAmount: 0,
    poDialogState: false,
    assignTaskFields: JSON.parse(JSON.stringify(assignTaskFields)),
    completionPerctangeTaskType: "",
    completedProgress: 0,
    invoiceProgress: 0,
    timeSpentProgress: 0,
    costToDate: 0,
    totalApprovedInvoices: 0,
    profitMarginValue: "",
    showStageSelect: false,
    accOpen: false,
    rows: [],
    rowFields: {},
    deleteopen: false,
    deleteId: "",

    material_Profit: 0,
    sub_Contractor_Total_Profit: 0,
    labor_total_Profit: 0,
    totalProfit: 0,
    optionsTotal: {},
    variationsTotal: {},
    materialTotalCost: 0,
    subcontractorTotal_Cost: 0,
    taskDialogState: false,
    changeSectionOpen: false,
    selectedSections: [],
    changeSectionTaskId: "",
    selectedTaskStageId: "",
    proposalDialogState: false,
    proposalType: '',
    subTaskData: [],
  });
  const [fieldOpsConfirmation, setFieldOpsConfirmation] = useState(
    initialFieldOpsConfirmation,
  );
  const { projectId } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectSummary = useSelector((state) => state.projectSummary) || {};
  const userData = useSelector((state) => state.user.userData) || {};
  const projectVariation = idx(projectSummary, (_) => _.projectVariation) || {};
  const sectionData = idx(projectSummary, (_) => _.sectionData) || [];
  const [sectionFields, setSectionFields] = useState({
    name: "section",
    label: "Select Section",
    value: [],
    items: [],
    isMulti: false,
    error: false,
    valid: true,
    touched: false,
    open: false,
    changeSectionTaskId: "",
    selectedTaskStageId: "",
    isSearchable: true,
    isStage: true,
    setDefaultOption: false,
    validation: [ValidatorTypes.REQUIRED, ValidatorTypes.MULTISELECT],
  });

  useEffect(() => {
    if (idx(projectSummary, (_) => _.sectionData)) {
      setSectionFields((prev) => ({
        ...prev,
        items: convertToReactSelectFormat(
          idx(projectSummary, (_) => _.sectionData) || []
        ),
      }));
    }
  }, [projectSummary]);

  const subTaskList = async () => {
    return await dispatch(getSubTaskList(projectId, { type: 'proposal' })).catch(e => console.error(e));
  };

  const proposalDialogHandler = async (proposalType = ProposalType.SUMMARY) => {
    let subTasks = null;
    // for the current detailed proposal, we don't need to get the sub tasks
    if (proposalType === ProposalType.DETAILED) {
      subTasks = await subTaskList();
    }

    setTaskListState(prev => ({
      ...prev,
      proposalType,
      proposalDialogState: !prev.proposalDialogState,
      subTaskData: proposalType === ProposalType.DETAILED ? subTasks : []
    }));
  };

  const convertOptiontoTask = () => {
    const { valid } = ValidateField({
      value: sectionFields.value,
      validation: sectionFields.validation,
    });
    setSectionFields((prev) => ({
      ...prev,
      error: !valid ? "Select at least one section" : false,
      valid,
      touched: true,
    }));
    if (!valid) return;

    const { currentEditId = {} } = taskListState;
    const { optionData = [], sectionData = [] } = projectSummary;
    const option = optionData.find(
      (item) => item.id === currentEditId.optionId
    );
    if (option) {
      let optionData = {
        revisionId: option.revisionId,
        optionId: option.id,
        stageId: sectionData.length ? taskListState.currentEditId.stageId : "",
      };

      _convertTask(optionData);
      handleConvertOptiontoTaskClose();
    }
  };

  const _convertTask = (data) => {
    dispatch(convertTask(data)).then((res) => {
      getProjectSummary({ projectId, dispatch });
      if (props.getStages) props.getStages();
    });
  };

  const handleConvertOptiontoTaskClose = () => {
    setTaskListState((prev) => ({
      ...prev,
      convertOptiontoTask: false,
      taskDialogState: false,
    }));
    setSectionFields((prev) => ({
      ...prev,
      value: [],
      error: false,
      valid: true,
      touched: false,
    }));
    handleMenuClose();
  };

  const stageSelectHandler = async (choice) => {
    dispatch(
      updateProjectAction({
        showStageSelect: !idx(projectSummary, (_) => _.project.showStageSelect),
        ...(!choice && { hasStages: false }),
      })
    );

    if (!choice) await getProjectSummary({ projectId, dispatch });
    // For Add Stage
    // if (idx(projectSummary, _ => _.project.hasStages))
    //     setTaskListState(prev => ({
    //         ...prev,
    //         stageDialogState: true
    //     }));
  };

  const deleteHandler = (id = "") => {
    markAsDeleted();
  };

  const deleteDescription = () => {
    const { deleteId, deleteType, sectionName = "" } = taskListState;
    if (deleteType === "Section" || deleteType === "Sub-Section") {
      return `${deleteType} - ${sectionName}`;
    } else {
      const tasks = [];
      const stages = extractLastStageData({
        stages: idx(projectSummary, (_) => _.sectionData) || [],
      });
      stages.forEach(
        (stage) =>
          idx(stage, (_) => _.tasks) &&
          tasks.push(...idx(stage, (_) => _.tasks))
      );
      const task = tasks.find((_) => _.id === deleteId) || {};
      const taskName = idx(task, (_) => _.name) || "";
      return `${deleteType} - ${taskName}? The parent task and all of its related subtasks will be deleted`;
    }
  };

  const markAsDeleted = () => {
    const { deleteId, deleteType } = taskListState;
    if (deleteId && deleteType) {
      if (deleteType == "Task" || deleteType == "OptionTask") {
        dispatch(
          deleteTask(deleteId, deleteType == "OptionTask" ? true : false)
        ).then((res) => {
          getProjectSummary({ projectId, dispatch });
        });
      } else if (deleteType == "Subtask" || deleteType == "OptionSubtask") {
        dispatch(
          deleteSubTask(deleteId, deleteType == "OptionSubtask" ? true : false)
        ).then((res) => {
          getProjectSummary({ projectId, dispatch });
        });
      } else if (deleteType == "Section" || deleteType === "Sub-Section") {
        _deleteStage(deleteId, deleteType);
      }

      deleteDialogHandler();
    }
  };

  const deleteDialogHandler = (id = "", type = "", sectionName = "") => {
    handleClose();
    setTaskListState((prev) => ({
      ...prev,
      deleteopen: !prev.deleteopen,
      deleteId: id,
      deleteType: type,
      sectionName: sectionName,
    }));
  };

  const handleClose = () => {
    setTaskListState((prev) => ({
      ...prev,
      anchorEl: null,
      menuItems: {},
    }));
    dispatch(
      updateProjectAction({
        hasStages: null,
      })
    );
  };

  const setCompletionPercentage = async (value) => {
    const {
      _stageId,
      _taskId,
      _subTaskId,
      _optionId,
      completionPerctangeTaskType,
    } = taskListState;

    if (!value) {
      this.props.warningSnackBar("Please Enter value");
      return;
    }

    const formData = new FormData();
    formData.set("percentageComplete", value);

    if (completionPerctangeTaskType === "STAGE") {
      await dispatch(updateStage(formData, _stageId));
      getProjectSummary({ projectId, dispatch });
    } else if (completionPerctangeTaskType === "TASK") {
      await dispatch(updateTask(formData, _taskId));
      getProjectSummary({ projectId, dispatch });
    } else if (completionPerctangeTaskType === "SUBTASK") {
      await dispatch(updateSubTask(formData, _subTaskId));
      getProjectSummary({ projectId, dispatch });
    } else if (completionPerctangeTaskType === "VARIATION") {
      await dispatch(updateOption(formData, _optionId));
      getOptions({ projectId, dispatch });
    }

    completionPercentageTaskHandler({});
  };

  const completionPercentageTaskHandler = ({
    type,
    percentageCompleted,
    stageId,
    taskId,
    subTaskId,
    optionId,
  }) => {
    setTaskListState((prev) => ({
      ...prev,
      showPercentageDialog: !prev.showPercentageDialog,
      _stageId: stageId,
      _taskId: taskId,
      _subTaskId: subTaskId,
      completionPerctangeTaskType: type,
      percentageCompleted,
      _optionId: optionId,
    }));
  };

  const assignTaskSave = async () => {
    const { assignTaskFields, taskData, id } = taskListState;
    const formData = new FormData();
    formData.set("status", assignTaskFields.status.value);
    formData.set(
      "percentageComplete",
      assignTaskFields.percentageComplete.value
    );
    formData.set("reminder", assignTaskFields.reminder.value);
    for (
      var i = 0;
      i < assignTaskFields.attachments.attachmentArr.length;
      i++
    ) {
      formData.append(
        "attachment[]",
        JSON.stringify(assignTaskFields.attachments.attachmentArr[i])
      );
    }

    for (var x = 0; x < assignTaskFields.attachments.value.length; x++) {
      formData.append("attachment", assignTaskFields.attachments.value[x]);
    }

    if (taskData.type === "stage") {
      await dispatch(updateStage(formData, id));
    } else if (taskData.type === "task") {
      await dispatch(updateTask(formData, id));
    } else if (taskData.type === "subTask") {
      await dispatch(updateSubTask(formData, id));
    }

    setTaskListState((prev) => ({
      ...prev,
      assignTaskEdit: false,
      assignTaskFields: JSON.parse(JSON.stringify(assignTaskFields)),
    }));
    navigate(`${PROJECT_MANAGEMENT}/view/${projectId}`);
  };

  const timeSheetHandler = () => {
    navigate(
      `/${PROJECT_MANAGEMENT}/view/${projectId}/timesheet/${taskListState.id}`
    );
  };

  const handleMenuClose = () => {
    setTaskListState((prev) => ({
      ...prev,
      anchorEl: null,
      currentEditType: "",
      currentEditId: {},
    }));
  };

  const handleStageClick = (
    stageId = null,
    parentStageId = null,
    nestedStageId = null,
    addStage = true
  ) => {
    setTaskListState((prev) => ({
      ...prev,
      stageDialogState: !prev.stageDialogState,
      stageId: stageId,
      parentStageId: parentStageId,
      nestedStageId: nestedStageId,
      addStage: addStage,
    }));
  };

  const handleTaskClick = ({
    isVariation = false,
    stageId = "",
    taskId = "",
    isSubTask = false,
    subTaskId = "",
    editTask = false,
    editSubTask = false,
    optionId = "",
    isOption = false,
    editOption = false,
    action = "new",
    selectedTemplateTask = null,
  }) => {
    dispatch(clearTaskAction());
    setTaskListState((prev) => ({
      ...prev,
      stageId,
      taskId,
      projectId: projectId,
      isSubTask,
      subTaskId,
      editTask,
      editSubTask,
      optionId,
      action,
      isOption,
      editOption,
      isVariation,
      taskDialogState: !prev.taskDialogState,
      proposalDialogState: false,
      selectedTemplateTask: selectedTemplateTask,
    }));
  };

  const optionDialogHandler = ({
    optionId = "",
    isOption = true,
    editOption = false,
    action = "new",
    stageId = "",
    taskId = "",
    isSubTask = false,
    subTaskId = "",
    editTask = false,
    editSubTask = false,
  }) => {
    dispatch(clearTaskAction());
    setTaskListState((prev) => ({
      ...prev,
      stageId,
      taskId,
      isSubTask,
      subTaskId,
      editTask,
      editSubTask,
      action,
      optionId,
      isOption,
      editOption,
      taskDialogState: !prev.taskDialogState,
      proposalDialogState: false,
      selectedTemplateTask: null,
    }));
  };

  const taskDialogHandler = (
    stageId = "",
    taskId = "",
    isSubTask = false,
    subTaskId = "",
    editTask = false,
    editSubTask = false,
    optionId = "",
    isOption = false,
    editOption = false,
    action = "new"
  ) => {
    setTaskListState((prev) => ({
      ...prev,
      stageId,
      taskId,
      isSubTask,
      subTaskId,
      editTask,
      editSubTask,
      optionId,
      action,
      isOption,
      editOption,
      taskDialogState: !prev.taskDialogState,
      proposalDialogState: false,
      selectedTemplateTask: null,
    }));
  };

  const _editProject = async () => {
    if (!projectId) return;
    navigate(`/${PROJECT_MANAGEMENT}/edit/${projectId}`);
  };
  const onDragEnd = async (result, stage) => {
    if (!result.destination) return;
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }

    const destinationid = result.destination.droppableId.replace("droppable", "");
    const ordering = await handleDragEnd(result, [stage]);

    const reorderedTasks = Array.from(stage.tasks || []);
    const [movedTask] = reorderedTasks.splice(result.source.index, 1);
    if (!movedTask) return;

    const updatedMovedTask = {
      ...movedTask,
      ordering_id: ordering.newOrderingId,
    };
    reorderedTasks.splice(result.destination.index, 0, updatedMovedTask);

    const updatedSectionData = updateStageTasksInTree(
      _.cloneDeep(sectionData),
      stage.id,
      reorderedTasks
    );
    dispatch(addSectionDataAction(updatedSectionData));

    try {
      await dispatch(
        updateTaskOrderingId(
          { ordering_id: ordering.newOrderingId },
          ordering.taskId,
          destinationid
        )
      );
    } catch (e) {
      console.error("Failed to update task ordering:", e);
    }
  };

  const handleOptionMenu = (
    event,
    type,
    stageId = "",
    taskId = "",
    subTaskId = "",
    optionId = ""
  ) => {
    let menuItem = {};
    const {
      project: { projectType },
    } = projectSummary;
    if (type === "OPTION") {
      if (projectType === "TENDER") menuItem.convert = "Convert to Task";
      menuItem.revise = "Create Revision";
    }
    const anchorEl = event.currentTarget;

    setTaskListState((prev) => ({
      ...prev,
      anchorEl: anchorEl,
      menuItems: menuItem,
      currentEditType: type,
      currentEditId: { stageId, taskId, subTaskId, optionId },
    }));
  };

  const handleMenuItemClick = async (type) => {
    const { currentEditType, currentEditId = {} } = taskListState;
    const stages =
      extractLastStageData({
        stages: idx(projectSummary, (_) => _.sectionData),
      }) || [];
    if (type === "add") {
      if (currentEditType === "STAGE") {
        taskDialogHandler(currentEditId.stageId);
      } else if (currentEditType === "TASK") {
        let hasSubTask = false;
        stages.forEach((stage) => {
          stage.tasks.filter((task) => {
            if (
              task.id === currentEditId.taskId &&
              task.stageId === currentEditId.stageId
            )
              hasSubTask = task.subTasks.length ? true : false;
            return task;
          });
        });
        if (hasSubTask) {
          taskDialogHandler(currentEditId.stageId, currentEditId.taskId, true);
        } else {
          setTaskListState((prev) => ({
            ...prev,
            subTaskConfirmModal: !prev.subTaskConfirmModal,
            proposalDialogState: false,
          }));
        }
      }
    } else if (type === "copy") {
      await dispatch(
        duplicateProjectTask(
          currentEditType,
          currentEditId.stageId,
          currentEditId.taskId,
          currentEditId.subTaskId
        )
      );
      getProjectSummary({ projectId, dispatch });
    } else if (type === "delete") {
      if (currentEditType === "STAGE") {
        _deleteStage(currentEditId.stageId);
      } else if (currentEditType === "TASK") {
        _deleteTask(currentEditId.taskId);
      } else if (currentEditType === "SUBTASK") {
        _deleteSubTask(currentEditId.subTaskId);
      }
    } else if (type === "convert") {
      setTaskListState((prev) => ({
        ...prev,
        convertOptiontoTask: !prev.subTaskConfirmModal,
        proposalDialogState: false,
        anchorEl: null,
        currentEditType: "",
      }));
    } else if (type === "revise") {
      createOptionRevision(currentEditId.optionId, currentEditId.stageId);
    }
  };

  const createOptionRevision = (id, stageId = "") => {
    handleMenuClose();
    optionDialogHandler({
      optionId: id,
      stageId,
      isOption: true,
      editOption: true,
      action: "revise",
    });
  };

  const _deleteStage = (id, deleteType) => {
    dispatch(deleteStage(id, deleteType))
      .then((res) => {
        getProjectSummary({ projectId, dispatch });
      })
      .catch((e) => console.error(e));
  };

  const _deleteTask = (id) => {
    dispatch(deleteTask(id)).then((res) => {
      getProjectSummary({ projectId, dispatch });
    });
  };
  const _deleteSubTask = (id) => {
    dispatch(deleteSubTask(id)).then((res) => {
      getProjectSummary({ projectId, dispatch });
    });
  };

  const handleTaskTemplateDialog = (id = "") => {
    setTaskListState((prev) => ({
      ...prev,
      selectedTaskTemplateStage: id,
      showTaskTemplateDialog: !prev.showTaskTemplateDialog,
    }));
  };

  const loadTaskFromTemplate = (selectedTask) => {
    handleTaskClick({
      isVariation: false,
      stageId: taskListState.selectedTaskTemplateStage,
      taskId: "",
      isSubTask: false,
      subTaskId: "",
      editTask: false,
      editSubTask: false,
      optionId: "",
      isOption: false,
      editOption: false,
      action: "new",
      selectedTemplateTask: selectedTask,
    });
    handleTaskTemplateDialog();
  };


  const updateSectionData = (flag, ids) => {
    const updateRecursive = (stages) => {
      return stages.map(stage => {
        let updatedStage = { ...stage };
        if (ids.includes(stage.id)) {
          updatedStage.is_attribution_level = flag;
        }
        if (stage.stages && stage.stages.length > 0) {
          updatedStage.stages = updateRecursive(stage.stages);
        }
        return updatedStage;
      });
    };

    const newSectionData = updateRecursive(sectionData);
    dispatch(addSectionDataAction(newSectionData));
  };

  const updateSectionExcludeData = (flag, ids) => {
    const updateRecursive = (stages) => {
      return stages.map(stage => {
        let updatedStage = { ...stage };
        if (ids.includes(stage.id)) {
          updatedStage.exclude_from_field_ops = flag;
        }
        if (stage.stages && stage.stages.length > 0) {
          updatedStage.stages = updateRecursive(stage.stages);
        }
        return updatedStage;
      });
    };

    const newSectionData = updateRecursive(sectionData);
    dispatch(addSectionDataAction(newSectionData));
  };

  const updateTaskExcludeData = (flag, ids) => {
    const updateRecursive = (stages) => {
      return stages.map((stage) => {
        const updatedStage = { ...stage };
        if (Array.isArray(stage.tasks)) {
          updatedStage.tasks = stage.tasks.map((task) =>
            ids.includes(task.id)
              ? {
                ...task,
                excludeFromFieldOps: flag,
                exclude_from_field_ops: flag,
              }
              : task,
          );
        }
        if (stage.stages && stage.stages.length > 0) {
          updatedStage.stages = updateRecursive(stage.stages);
        }
        return updatedStage;
      });
    };

    const newSectionData = updateRecursive(sectionData);
    dispatch(addSectionDataAction(newSectionData));
  };

  const handleToggleAttributionLevel = async (stageId, attributionLevel, projectId) => {
    try {
      const result = await dispatch(updateStageAttributionLevel(stageId, attributionLevel, projectId));

      if (result) {
        const { ids, is_attribution_level } = result;

        // If unchecking attribution (setting to false), cascade to all children
        if (!is_attribution_level) {
          const allChildIds = [];
          const collectChildren = (stages, parentIds = ids) => {
            stages.forEach(stage => {
              // Check if this stage is a child of any parent
              if (parentIds.includes(stage.parent_stage_id)) {
                allChildIds.push(stage.id);
                // Recursively collect children of this stage
                if (stage.stages && stage.stages.length > 0) {
                  collectChildren(stage.stages, [stage.id]);
                }
              }
              // Also check if this stage has nested stages that are children
              if (stage.stages && stage.stages.length > 0) {
                stage.stages.forEach(nestedStage => {
                  if (parentIds.includes(nestedStage.parent_stage_id) || parentIds.includes(stage.id)) {
                    if (!allChildIds.includes(nestedStage.id)) {
                      allChildIds.push(nestedStage.id);
                      // Recursively collect children of this nested stage
                      if (nestedStage.stages && nestedStage.stages.length > 0) {
                        collectChildren(nestedStage.stages, [nestedStage.id]);
                      }
                    }
                  }
                });
              }
            });
          };
          collectChildren(sectionData);

          // Update UI for parent and all children
          updateSectionData(false, [...ids, ...allChildIds]);
        } else {
          // Normal update for checking attribution
          updateSectionData(is_attribution_level, ids);
        }

        // Refresh project summary to recalculate aggregated claims and percentages
        // updateProjectSummaryData({ projectId, dispatch, type: props.type });
      }

    } catch (error) {
      console.error('Error dispatching updateStageAttributionLevel:', error);
    }
  };

  /**
   * Toggles the field operations exclusion flag for a section/stage.
   *
   * @param {string} stageId - The stage/section identifier.
   * @param {boolean} excludeFromFieldOps - The new exclusion flag value.
   * @returns {Promise<void>}
   */
  const handleToggleFieldOpsExclusion = async (stageId, excludeFromFieldOps) => {
    try {
      const formData = new FormData();
      formData.set("exclude_from_field_ops", excludeFromFieldOps);
      formData.set("skipS3", true);
      await dispatch(updateStage(formData, stageId));
      updateSectionExcludeData(excludeFromFieldOps, [stageId]);
    } catch (error) {
      console.error("Error updating section field ops exclusion:", error);
    }
  };

  /**
   * Toggles the field operations exclusion flag for a task.
   *
   * @param {string} taskId - The task identifier.
   * @param {boolean} excludeFromFieldOps - The new exclusion flag value.
   * @returns {Promise<void>}
   */
  const handleToggleTaskFieldOpsExclusion = async (
    taskId,
    excludeFromFieldOps,
  ) => {
    try {
      const formData = new FormData();
      formData.set("excludeFromFieldOps", excludeFromFieldOps);
      await dispatch(updateTask(formData, taskId));
      updateTaskExcludeData(excludeFromFieldOps, [taskId]);
    } catch (error) {
      console.error("Error updating task field ops exclusion:", error);
    }
  };

  const closeFieldOpsConfirmation = () => {
    setFieldOpsConfirmation(initialFieldOpsConfirmation);
  };

  const openFieldOpsConfirmation = ({
    entityType,
    entityId,
    nextValue,
    entityName,
  }) => {
    setFieldOpsConfirmation({
      open: true,
      entityType,
      entityId,
      nextValue,
      entityName,
    });
  };

  const handleRequestStageFieldOpsToggle = (stageId, nextValue, stageName) => {
    openFieldOpsConfirmation({
      entityType: "stage",
      entityId: stageId,
      nextValue,
      entityName: stageName,
    });
  };

  const handleRequestTaskFieldOpsToggle = (taskId, nextValue, taskName) => {
    openFieldOpsConfirmation({
      entityType: "task",
      entityId: taskId,
      nextValue,
      entityName: taskName,
    });
  };

  const handleRequestVariationFieldOpsToggle = (
    variationId,
    nextValue,
    variationName,
  ) => {
    openFieldOpsConfirmation({
      entityType: "variation",
      entityId: variationId,
      nextValue,
      entityName: variationName,
    });
  };

  const confirmFieldOpsToggle = async () => {
    if (!fieldOpsConfirmation.entityId) return;

    if (fieldOpsConfirmation.entityType === "stage") {
      await handleToggleFieldOpsExclusion(
        fieldOpsConfirmation.entityId,
        fieldOpsConfirmation.nextValue,
      );
    }

    if (fieldOpsConfirmation.entityType === "task") {
      await handleToggleTaskFieldOpsExclusion(
        fieldOpsConfirmation.entityId,
        fieldOpsConfirmation.nextValue,
      );
    }

    if (fieldOpsConfirmation.entityType === "variation") {
      await handleToggleVariationFieldOps(
        fieldOpsConfirmation.entityId,
        fieldOpsConfirmation.nextValue,
      );
    }

    closeFieldOpsConfirmation();
  };

  /**
   * Toggles the exclude_from_field_ops flag for a variation/option.
   *
   * Uses the shared updateOption action to persist the flag on the backend and then
   * refreshes the project summary so variation data and totals stay in sync in the UI.
   *
   * @param {string} optionId - The variation/option identifier.
   * @param {boolean} excludeFromFieldOps - The new exclusion flag value.
   */
  const handleToggleVariationFieldOps = async (optionId, excludeFromFieldOps) => {
    try {
      const formData = new FormData();
      formData.set("excludeFromFieldOps", excludeFromFieldOps);
      await dispatch(updateOption(formData, optionId, "isVariation"));
      const currentOptions = idx(projectSummary, (_) => _.optionData) || [];
      const updatedOptions = currentOptions.map((opt) =>
        opt.id === optionId
          ? {
              ...opt,
              excludeFromFieldOps,
              exclude_from_field_ops: excludeFromFieldOps,
            }
          : opt
      );
      dispatch(addOptionDataAction(updatedOptions));
    } catch (error) {
      console.error("Error updating variation field ops exclusion:", error);
    }
  };

  const stageTotals = idx(projectSummary, (_) => _.sections) || {};
  const sectionHeader = sectionData;
  const optionsTotal = idx(projectSummary, (_) => _.options) || {};
  const optionsToProjects = (
    idx(projectSummary, (_) => _.optionData) || []
  ).filter((_) => !_.stageId);

  // Combine both project-level and stage-mapped variations for bottom area display, removing duplicates
  const allVariations = [
    ...(projectVariation || []),
    ...(idx(projectSummary, (_) => _.variationData) || []),
  ];
  const variationToProjects = allVariations
    .filter((variation) => variation.proposal_status === 'APPROVED')
    .filter(
      (variation, index, self) =>
        index === self.findIndex((v) => v.id === variation.id)
    );

  const combinedOptionList = {
    // Start with existing project-level variation costs
    ...(idx(optionsTotal, (_) => _.stageSummary.projectlevel.optionList) || {}),
    // Add all stage-level variation costs
    ...Object.keys(stageTotals).reduce((acc, stageId) => {
      const stageVariationCosts =
        idx(optionsTotal, (_) => _.stageSummary[stageId].optionList) || {};
      return { ...acc, ...stageVariationCosts };
    }, {}),
  };

  const enhancedOptionsTotal = {
    ...optionsTotal,
    projectTotal:
      props.type === "project-view" && optionsTotal.approvedProjectTotal
        ? optionsTotal.approvedProjectTotal
        : idx(optionsTotal, (_) => _.projectTotal) || {},
    // Preserve the stageSummary structure
    stageSummary: {
      ...optionsTotal.stageSummary,
      projectlevel: {
        ...(idx(optionsTotal, (_) => _.stageSummary.projectlevel) || {}),
        optionList: combinedOptionList,
      },
    },
    // Also add the combined optionList at root level for individual variation lookup
    optionList: combinedOptionList,
  };

  const projectTotalCost = idx(projectSummary, (_) => _.projectTotal) || {};

  const revisedProjectTotalCost =
    idx(projectSummary, (_) => _.revisedProjectTotal) || {};
  const isConvertedToCurrentProject =
    idx(projectSummary, (_) => _.project.quoteType) === "quote" &&
    idx(projectSummary, (_) => _.project.projectType) === "PROJECT";

  const projectTotal = getFormatedData(projectTotalCost);
  const revisedProjectTotal = getFormatedData(revisedProjectTotalCost);

  const sectionChangeHandler = (selectedSection) => {
    setSectionFields((prev) => ({
      ...prev,
      value: selectedSection,
    }));
  };

  const sectionChangeCloseHandler = () => {
    setSectionFields((prev) => ({
      ...prev,
      value: [],
      error: false,
      valid: true,
      touched: false,
      open: !prev.open,
      mode: "move",
      isMulti: false,
      changeSectionTaskId: "",
      selectedTaskStageId: "",
      changeType: "",
      setDefaultOption: false,
    }));
  };

  const moveToNewSection = () => {
    const { value, changeSectionTaskId, validation } = sectionFields;
    const { valid } = ValidateField({ value, validation: validation });
    setSectionFields((prev) => ({
      ...prev,
      error: !valid ? "Select at least one section" : false,
      valid,
      touched: true,
    }));
    if (!valid) return;

    if (!Array.isArray(value)) {
      dispatch(
        changeSection({
          stageId: value.value,
          taskId: changeSectionTaskId,
          type: sectionFields.changeType,
        })
      )
        .then((res) => {
          sectionChangeCloseHandler();
          getProjectSummary({ projectId, dispatch });
        })
        .catch((e) => console.error(e));
    } else {
      dispatch(
        copySection({
          stageIds: value,
          taskId: changeSectionTaskId,
          type: sectionFields.changeType,
        })
      )
        .then((res) => {
          sectionChangeCloseHandler();
          getProjectSummary({ projectId, dispatch });
        })
        .catch((e) => console.error(e));
    }
  };

  const copyTasksToSection = (id, stageId, changeType) => {
    setSectionFields((prev) => ({
      ...prev,
      changeSectionTaskId: id,
      selectedTaskStageId: stageId,
      open: !prev.open,
      changeType: changeType,
      mode: "copy", // Set mode to copy
      isMulti: true, // Enable multi-select
      setDefaultOption: changeType === "Variation" && stageId ? true : false,
      items: convertToReactSelectFormat(sectionData || [], stageId),
    }));
  };

  const switchSection = (id, stageId, changeType) => {
    setSectionFields((prev) => ({
      ...prev,
      changeSectionTaskId: id,
      selectedTaskStageId: stageId,
      open: !prev.open,
      changeType: changeType,
      mode: "move",
      isMulti: false,
      setDefaultOption: changeType === "Variation" && stageId ? true : false,
      items: convertToReactSelectFormat(sectionData || [], stageId),
    }));
  };

  const onDragEndSections = async (result, stages = []) => {
    if (!result.destination) return; // Dropped outside the list
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }

    const reorderedSections =
      stages.length > 0 ? _.cloneDeep(stages) : Array.from(sectionData);
    const [movedSection] = reorderedSections.splice(result.source.index, 1);
    if (!movedSection) return;

    reorderedSections.splice(result.destination.index, 0, movedSection);

    const updatedReorderedSections = reorderedSections.map((section, index) => ({
      ...section,
      ordering_id: index + 1,
    }));

    const stagesData = updatedReorderedSections.map((section) => ({
      id: section.id,
      ordering_id: section.ordering_id,
    }));

    if (stages.length > 0 && movedSection.parent_stage_id) {
      const newSectionData = updateNestedStagesInTree(
        _.cloneDeep(sectionData),
        movedSection.parent_stage_id,
        updatedReorderedSections
      );
      dispatch(addSectionDataAction(newSectionData));
    } else {
      dispatch(addSectionDataAction(updatedReorderedSections));
    }

    try {
      await dispatch(batchUpdateStageOrderingId({ stages: stagesData }));
    } catch (e) {
      console.error("Failed to update section ordering:", e);
    }
  };

  const getProjectSummary = async ({ projectId, dispatch }) => {
    await getProjectSummaryData({ projectId, dispatch, type: props.type, forceFresh: true }).catch((e) =>
      console.error(e)
    );
  };

  return (
    <div className="viewProject">
      <Menu
        id="simple-menu"
        anchorEl={taskListState.anchorEl}
        keepMounted
        open={Boolean(taskListState.anchorEl)}
        onClose={handleMenuClose}
      >
        {Object.entries(taskListState.menuItems).map(([key, value], index) => (
          <MenuItem
            key={index}
            disabled={!updatePermission}
            onClick={() => handleMenuItemClick(key)}
          >
            {value}
          </MenuItem>
        ))}
      </Menu>
      {taskListState.proposalDialogState && <ProposalDialog
        type={taskListState.proposalType}
        projectName={idx(projectSummary, _ => _.project.projectName)}
        customerName={idx(projectSummary, _ => _.project.customerName) || ""}
        projectId={projectId}
        projectDescription={idx(projectSummary, _ => _.project.projectDescription) || ""}
        open={taskListState.proposalDialogState}
        dialogClose={() => proposalDialogHandler()}
        stages={JSON.parse(JSON.stringify(idx(projectSummary, _ => _.sectionData) || []))}
        options={idx(projectSummary, _ => _.optionData) || []}
        isVariation={false}
        stageTotal={idx(projectSummary, _ => _.sections) || {}}
        optionTotal={idx(projectSummary, _ => _.options) || {}}
        projectTotal={idx(projectSummary, _ => _.projectTotal) || {}}
        subTaskList={taskListState.subTaskData}
      />}
      {taskListState.convertOptiontoTask && (
        <Modal
          open={taskListState.convertOptiontoTask}
          onClose={handleConvertOptiontoTaskClose}
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          fullWidth={false}
          maxWidth="lg"
          modalHeading="Convert Option to Task"
          modalSaveTxt="Yes"
          modalCloseTxt="No"
          onClick={convertOptiontoTask}
          showActionBtn
          savebtnClass="createBtn"
          primary
        >
          <div>Select the section to convert this option into a task.</div>
          <div className="my-3">
            <MultiSelect
              {...sectionFields}
              onChangeHandler={(e) => {
                sectionChangeHandler(e);
                setTaskListState((prev) => ({
                  ...prev,
                  currentEditId: {
                    ...prev.currentEditId,
                    stageId: e.value,
                  },
                }));
              }}
            />
          </div>
        </Modal>
      )}
      {taskListState.stageDialogState && (
        <AddStage
          {...props}
          projectName={idx(projectSummary, (_) => _.project.projectName)}
          customerName={
            idx(projectSummary, (_) => _.project.customerName) || ""
          }
          projectId={projectId}
          open={taskListState.stageDialogState}
          dialogClose={() => handleStageClick()}
          stageId={taskListState.stageId}
          addStage={taskListState.addStage}
          parentStageId={taskListState.parentStageId}
          nestedStageId={taskListState.nestedStageId}
          totalStageCost={
            (idx(
              projectSummary,
              (_) => _.sections[taskListState.stageId].materialSaleAmount
            ) || 0) +
            (idx(
              projectSummary,
              (_) => _.sections[taskListState.stageId].laborSaleAmount
            ) || 0)
          }
          getStages={() => getProjectSummary({ projectId, dispatch })}
          userAccessControl={props.userAccessControl}
          stageTotals={stageTotals}
          selectedSections={
            findStageData(sectionData, taskListState.stageId) || []
          }
        />
      )}
      {idx(projectSummary, (_) => _.project.showStageSelect) && (
        <StageSelectDialog
          projectId={projectId}
          open={idx(projectSummary, (_) => _.project.showStageSelect)}
          dialogClose={stageSelectHandler}
        />
      )}
      {taskListState.taskDialogState && !taskListState.isVariation && (
        <AddTask
          {...props}
          open={taskListState.taskDialogState}
          dialogClose={() => handleTaskClick({})}
          taskTemplateData={taskListState.selectedTemplateTask}
          projectName={idx(projectSummary, (_) => _.project.projectName)}
          customerName={
            idx(projectSummary, (_) => _.project.customerName) || ""
          }
          projectId={projectId}
          handleConvertOptiontoTaskClose={() =>
            handleConvertOptiontoTaskClose()
          }
          profitMargin={idx(
            projectSummary,
            (_) => _.project.projectMarginValue
          )}
          stageId={taskListState.stageId}
          projectType={idx(projectSummary, (_) => _.project.projectType)}
          taskId={taskListState.taskId}
          isSubTask={taskListState.isSubTask}
          isOption={taskListState.isOption}
          isVariation={taskListState.isVariation}
          optionId={taskListState.optionId}
          editOption={taskListState.editOption}
          subTaskId={taskListState.subTaskId}
          action={taskListState.action}
          currentTask={taskListState.currentTask}
          currentTaskCost={
            (idx(projectSummary, (_) => _.sections[taskListState.stageId]) ||
              {})[taskListState.currentTask?.id] || {}
          }
          customerLevel={idx(projectSummary, (_) => _.project.customerLevel)}
          editTask={taskListState.editTask}
          editSubTask={taskListState.editSubTask}
          subcontractors={idx(projectSummary, (_) => _.subcontractors) || []}
          getStages={() => getProjectSummary({ projectId, dispatch })}
          options={idx(projectSummary, (_) => _.optionData) || []}
          liststages={true}
          listvariation={true}
          userAccessControl={props.userAccessControl}
          updatePermission={updatePermission}
        />
      )}
      {taskListState.taskDialogState && taskListState.isVariation && (
        <AddVariation
          {...props}
          open={taskListState.taskDialogState}
          dialogClose={() => handleTaskClick({})}
          taskTemplateData={taskListState.selectedTemplateTask}
          projectName={idx(projectSummary, (_) => _.project.projectName)}
          customerName={
            idx(projectSummary, (_) => _.project.customerName) || ""
          }
          projectId={projectId}
          handleConvertOptiontoTaskClose={() =>
            handleConvertOptiontoTaskClose()
          }
          profitMargin={idx(
            projectSummary,
            (_) => _.project.projectMarginValue
          )}
          stageId={taskListState.stageId}
          projectType={idx(projectSummary, (_) => _.project.projectType)}
          taskId={taskListState.taskId}
          isSubTask={taskListState.isSubTask}
          isOption={taskListState.isOption}
          isVariation={taskListState.isVariation}
          optionId={taskListState.optionId}
          editOption={taskListState.editOption}
          subTaskId={taskListState.subTaskId}
          action={taskListState.action}
          currentTask={taskListState.currentTask}
          currentTaskCost={
            (idx(projectSummary, (_) => _.sections[taskListState.stageId]) ||
              {})[taskListState.currentTask?.id] || {}
          }
          customerLevel={idx(projectSummary, (_) => _.project.customerLevel)}
          editTask={taskListState.editTask}
          editSubTask={taskListState.editSubTask}
          subcontractors={idx(projectSummary, (_) => _.subcontractors) || []}
          getStages={() => getProjectSummary({ projectId, dispatch })}
          options={idx(projectSummary, (_) => _.optionData) || []}
          liststages={true}
          listvariation={true}
          userAccessControl={props.userAccessControl}
          updatePermission={updatePermission}
        />
      )}
      {taskListState.deleteopen && (
        <DeleteDialog
          open={taskListState.deleteopen}
          deleteId={taskListState.deleteId}
          deleteType={taskListState.deleteType}
          deleteHandler={() => deleteHandler()}
          dialogClose={() => deleteDialogHandler()}
          moduleName={taskListState.deleteType}
          description={deleteDescription()}
        />
      )}
      {sectionFields.open && (
        <SectionChangeModal
          {...sectionFields}
          onChangeHandler={sectionChangeHandler}
          sectionChangeCloseHandler={sectionChangeCloseHandler}
          moveToNewSection={moveToNewSection}
          moduleName={"Change Section"}
        />
      )}
      {taskListState.showPercentageDialog && (
        <CompletionPercentageDialog
          dialogClose={completionPercentageTaskHandler}
          setCompletionPercentage={setCompletionPercentage}
          percentageCompleted={taskListState.percentageCompleted}
          open={taskListState.showPercentageDialog}
        />
      )}
      <Modal
        open={fieldOpsConfirmation.open}
        onClose={closeFieldOpsConfirmation}
        modalHeading="Confirm Field Ops Visibility Change"
        modalSaveTxt="Confirm"
        modalCloseTxt="Cancel"
        onClick={confirmFieldOpsToggle}
        showActionBtn={true}
        data-cy="field-ops-confirmation-modal"
      >
        <p className="mb-0">
          {`${fieldOpsConfirmation.entityName || "This item"} will be ${fieldOpsConfirmation.nextValue ? "excluded from" : "included in"} field ops once you confirm.`}
        </p>
      </Modal>
      {taskListState.showTaskTemplateDialog && (
        <ListTaskTemplate
          open={taskListState.showTaskTemplateDialog}
          dialogClose={handleTaskTemplateDialog}
          saveHandler={() => {
            getProjectSummary({ projectId, dispatch });
            handleTaskTemplateDialog();
          }}
          addHandler={loadTaskFromTemplate}
          stageId={taskListState.selectedTaskTemplateStage}
          profitMargin={idx(
            projectSummary,
            (_) => _.project.projectMarginValue
          )}
        />
      )}
      <div className="view-project-page">
        <div className="overviewContainer">
          <Paper className="main">
            <Routes>
              <Route
                path={`assigntask/:id/:taskType`}
                // path={`${PROJECT_MANAGEMENT}/${VIEW_PROJECT}/assigntask/:id/:taskType`}
                element={
                  <Grid container style={{ marginBottom: "20px" }}>
                    <Grid xs={4} className="projectName" item>
                      {idx(projectSummary, (_) => _.project.projectName)}
                    </Grid>
                    <Grid xs={8} item className="headerInfo">
                      <Grid xs={6} item>
                        <div className="projectInfo">
                          {idx(projectSummary, (_) => _.project.hasStages) && (
                            <p>
                              Stages:{" "}
                              <b>
                                {
                                  (
                                    idx(projectSummary, (_) => _.sectionData) ||
                                    []
                                  ).length
                                }
                              </b>
                            </p>
                          )}
                          <p>
                            Tasks:{" "}
                            <b>
                              {idx(
                                projectSummary,
                                (_) => _.project.totalTasks
                              ) || 0}
                            </b>
                          </p>
                        </div>
                      </Grid>
                      <Grid xs={6} item style={{ textAlign: "right" }}>
                        <div className="stockHeader">
                          <p className="cancel-btn" onClick={assignTaskSave}>
                            Save
                          </p>
                          <p className="cancel-btn" onClick={timeSheetHandler}>
                            Update TimeSheet
                          </p>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                }
              />
              <Route
                path=""
                element={
                  <>
                    <Card className="border-0">
                      <CardHeader
                        className="sections d-flex justify-content-between align-items-center bg-body"
                        data-cy="sections-header"
                      >
                        <h6 className="mb-0">SECTIONS</h6>
                        <div className="d-flex gap-2">
                          {/* Profit Rounding Button Row - Only for TENDER projects */}
                          {idx(projectSummary, _ => _.project.projectType) === "TENDER" && (
                            <Button
                              type="button"
                              className="btn-bg-brand-2 primarybtn"
                              data-cy="profit-rounding-btn"
                              onClick={() => setTaskListState(prev => ({ ...prev, profitRoundingModalOpen: true }))}
                            >
                              PROFIT ROUNDING
                            </Button>
                          )}
                          {writePermission &&
                            idx(
                              projectSummary,
                              (_) => _.project.projectType
                            ) === "TENDER" && (
                              <Button
                                type="submit"
                                className="ms-auto task-save-btn secondarybtn"
                                data-cy={"edit-tender-btn"}
                                onClick={() => _editProject()}
                              >
                                EDIT TENDER
                              </Button>
                            )}
                          {writePermission &&
                            idx(
                              projectSummary,
                              (_) => _.project.projectType
                            ) === "TENDER" && (
                              <Button
                                type="submit"
                                className="ms-auto btn-bg-brand-2 primarybtn"
                                data-cy={"add-option-btn"}
                                onClick={() =>
                                  optionDialogHandler({ stageId: "" })
                                }
                              >
                                ADD OPTION
                              </Button>
                            )}
                          {updatePermission && !isConvertedToCurrentProject && (
                            <Button
                              type="submit"
                              className="ms-auto add-task-btn secondarybtn"
                              data-cy={"add-section-btn"}
                              onClick={() => handleStageClick()}
                            >
                              ADD SECTION
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardBody
                        className="d-flex flex-column flex-wrap gap-2 p-0 pt-2"
                        data-cy="sections-container"
                      >
                        <DragDropContext onDragEnd={onDragEndSections}>
                          <Droppable droppableId="sections">
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                data-cy="sections-droppable"
                              >
                                {!_.isEmpty(stageTotals) &&
                                  !_.isEmpty(optionsTotal) &&
                                  (sectionData || []).map((stage, index) => (
                                    <Draggable
                                      key={stage.id}
                                      draggableId={stage.id}
                                      index={index}
                                      isDragDisabled={!updatePermission} // Disable dragging if no update permission
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          style={{
                                            ...provided.draggableProps.style,
                                            marginBottom: "8px", // Optional: spacing between sections
                                          }}
                                        >
                                          <StageAccordionTaskList
                                            key={`${stage.id}`}
                                            stage={stage}
                                            stagesLength={sectionData.length}
                                            dragHandleProps={
                                              provided.dragHandleProps
                                            }
                                            isDragging={snapshot.isDragging}
                                            sectionHeader={sectionHeader}
                                            isGlobalUser={userData.global_user}
                                            data-cy={`section-accordion-${stage.id}`}
                                            handleToggleAttributionLevel={handleToggleAttributionLevel}
                                            onRequestStageFieldOpsToggle={handleRequestStageFieldOpsToggle}
                                            onRequestTaskFieldOpsToggle={handleRequestTaskFieldOpsToggle}
                                            onRequestVariationFieldOpsToggle={handleRequestVariationFieldOpsToggle}

                                            {...{
                                              index,
                                              projectSummary,
                                              handleStageClick,
                                              handleTaskClick,
                                              handleTaskTemplateDialog,
                                              optionDialogHandler,
                                              deleteDialogHandler,
                                              handleOptionMenu,
                                              taskListState,
                                              writePermission,
                                              financePermission,
                                              updatePermission,
                                              _editProject,
                                              onDragEnd,
                                              onDragEndSections,
                                              projectId,
                                              switchSection,
                                              copyTasksToSection,
                                              stageTotals,
                                              optionsTotal,
                                              isConvertedToCurrentProject,
                                            }}
                                          />
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                        {idx(projectSummary, (_) => _.project.projectType) ===
                          "TENDER" ? (
                          <ul
                            className="accordion-icon stage-total-row tabel-list d-flex justify-content-sm-between list-unstyled"
                            data-cy="project-total-row"
                          >
                            <span className="section-header-spacer" aria-hidden="true" />
                            <li className="first-cell text-start mt-auto mb-auto me-auto  tender-task-total">
                              <span></span>
                              <h6 className="mb-0 ms-2">Project Total</h6>
                            </li>
                            {/* <li className="text-center mt-4 profit list-group-item d-flex flex-column fst-italic">
                              <span className="mt-3">Profit</span>
                            </li> */}
                            <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px mb-3">
                              <div data-cy="project-planned-hours" className="d-flex flex-column align-items-end">
                                <div className="brand-color-1 fw-bold" data-cy={'label'}>Planned Effort</div>
                                <div className="fw-bold" data-cy={'value'}>
                                  {sectionData.reduce((total, section) => total + (section.forecastHours || 0), 0).toFixed(1)} hrs
                                </div>
                              </div>
                            </li>
                            <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px" data-cy="material">
                              <span className="brand-color-1 fw-bold" data-cy={'label'}>Material</span>
                              <h6 className="mb-0" data-cy="sale-amount">{projectTotal.materialSaleAmount}</h6>
                              <span className="profit-percent fst-12">{projectTotal.materialProfit} ({projectTotal.materialProfitPercentage})</span>
                            </li>
                            <li
                              className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px"
                              data-cy="contractor"
                            >
                              <span className="brand-color-1 fw-bold" data-cy={'label'}>Contractor</span>
                              <h6 className="mb-0 fw-bold" data-cy="sale-amount">{projectTotal.subcontractorSaleAmount}</h6>
                              <span className="profit-percent fst-12">{projectTotal.subcontractorProfit} ({projectTotal.subcontractorProfitPercentage})</span>
                            </li>
                            <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px" data-cy="resource">
                              <span className="brand-color-1 fw-bold" data-cy={'label'}>Resource</span>
                              <h6 className="mb-0" data-cy="sale-amount">{projectTotal.laborSaleAmount}</h6>
                              <span className="profit-percent fst-12">{projectTotal.laborProfit} ({projectTotal.laborProfitPercentage})</span>
                            </li>
                            <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-center w-180px" data-cy="total">
                              <span className="brand-color-1 fw-bold" data-cy={'label'}>Sales</span>
                              <span className="brand-color-1 fw-bold" data-cy={'label'}>Profit (%)</span>
                              <h6 className="mb-0" data-cy="sale-amount">{projectTotal.totalSaleAmount}</h6>
                              <span className="profit-percent fst-12">{projectTotal.totalProfit} ({projectTotal.totalProfitPercentage})</span>
                            </li>
                            {/* Placeholder columns to match section header lock/wbs columns for alignment */}
                              <li className="d-flex flex-column justify-content-center align-items-end text-center wbs-lock-label wbs-mapping" />
                              {/* <li className="text-center tender-task-total wbs-mapping" />
                            <li className="text-center tender-task-total wbs-mapping" /> */}
                              <li className="action-icons-task pe-3 text-end w-5" />
                          </ul>
                        ) : (
                          <ul
                            className="accordion-icon stage-total-row tabel-list d-flex justify-content-sm-between list-unstyled"
                            data-cy="project-total-row"
                          >
                            <span className="section-header-spacer" aria-hidden="true" />
                            <li className="first-cell text-start mt-auto mb-auto me-auto tender-task-total w-25">
                              <span></span>
                              <h6 className="mb-0 ms-2">Project Total</h6>
                            </li>
                            {projectTotal.completionPercentage && (
                              <>
                                {financePermission && (
                                  <>
                                    <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px mt-auto" data-cy="material">
                                      <span className="brand-color-1 fw-bold" data-cy={'label'}>Material</span>
                                      <h6 className="mb-0" data-cy="sale-amount">{projectTotal.materialSaleAmount}</h6>
                                      <span className="profit-percent fst-12 fw-bold">{projectTotal.materialProfit} ({projectTotal.materialProfitPercentage})</span>
                                    </li>
                                    <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px mt-auto" data-cy="contractor">
                                      <span className="brand-color-1 fw-bold" data-cy={'label'}>Contractor</span>
                                      <h6 className="mb-0 fw-bold" data-cy="sale-amount">{projectTotal.subcontractorSaleAmount}</h6>
                                      <span className="profit-percent fst-12 fw-bold">{projectTotal.subcontractorProfit} ({projectTotal.subcontractorProfitPercentage})</span>
                                    </li>
                                    <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px mt-auto" data-cy="resource">
                                      <span className="brand-color-1 fw-bold" data-cy={'label'}>Resource</span>
                                      <h6 className="mb-0" data-cy="sale-amount">{projectTotal.laborSaleAmount}</h6>
                                      <span className="profit-percent fst-12 fw-bold">{projectTotal.laborProfit} ({projectTotal.laborProfitPercentage})</span>
                                    </li>
                                  </>
                                )}
                                <li className="text-end w-150px mt-auto">
                                  <div data-cy="project-effort" className="d-flex flex-column flex-start-end align-items-end">
                                    <span className="brand-color-1 fw-bold" data-cy={'label'}>Effort</span>
                                    <h6 className="mb-0 fw-bold" data-cy={'value'}>
                                      {projectTotal.forecastHoursDisplay}
                                    </h6>
                                    <span className="profit-percent fst-12 fw-bold" data-cy={'value'}>
                                      {projectTotal.actualHoursDisplay} ({projectTotal.effortCompletionPercentage})
                                    </span>
                                  </div>
                                </li>
                                <li className="tender-task-total text-end w-150px mt-auto">
                                  <div data-cy="project-completion" className="d-flex flex-column align-items-end">
                                    <span className="brand-color-1 fw-bold" data-cy={'label'}>Completion %</span>
                                    <h6 className="mb-0 fw-bold" data-cy={'value'}>{projectTotal.completionPercentage}</h6>
                                  </div>
                                </li>
                              </>
                            )}
                            <li data-cy="sales-total-profit" className="d-flex flex-column flex-start-end justify-content-center align-items-end tender-task-total text-center w-180px">
                              <span className="brand-color-1 fw-bold">Sales</span>
                              <span className="brand-color-1 fw-bold">Profit (%)</span>

                              <h6 className="mb-0" data-cy="sale-amount">
                                {projectTotal.totalSaleAmount}
                              </h6>
                              <span className="profit-percent fst-12 fw-bold">
                                {projectTotal.totalProfit} ({projectTotal.totalProfitPercentage})</span>
                            </li>
                            {/* Placeholder columns to match section header lock/wbs columns for alignment */}
                            <li className="d-flex flex-column justify-content-center align-items-end text-center wbs-lock-label wbs-mapping" />
                            {/* <li className="text-center tender-task-total wbs-mapping" />
                            <li className="text-center tender-task-total wbs-mapping" /> */}
                            <li className="action-icons-task pe-3 text-end w-5" />
                          </ul>
                        )}
                      </CardBody>
                    </Card>
                    {variationToProjects.length ? (
                      <Card className="border-0 mt-3">
                        <CardHeader
                          className="sections d-flex justify-content-between align-items-center bg-body"
                          data-cy="variations-header"
                        >
                          <h6 className="mb-0 text-uppercase">Variations</h6>
                        </CardHeader>
                        <CardBody
                          className="d-flex flex-column flex-wrap gap-2 p-0 pt-2"
                          data-cy="variations-container"
                        >
                          <Paper className="paperContainer viewProjectCol">
                            <div className="projectableContainer">
                              <div
                                className="privewTable"
                                data-cy={"variation-table"}
                              >
                                <DragDropContext onDragEnd={onDragEnd}>
                                  <Tasks
                                    variations={variationToProjects}
                                    handleTaskClick={handleTaskClick}
                                    deleteDialogHandler={deleteDialogHandler}
                                    switchSection={switchSection}
                                    copyTasksToSection={copyTasksToSection}
                                    optionDialogHandler={optionDialogHandler}
                                    handleOptionMenu={handleOptionMenu}
                                    projectType={idx(projectSummary, (_) => _.project.projectType)}
                                    financePermission={financePermission}
                                    updatePermission={updatePermission}
                                    writePermission={writePermission}
                                    quoteType={idx(projectSummary, (_) => _.project.quoteType)}
                                    optionsTotal={enhancedOptionsTotal}
                                    toggleId={taskListState.toggleId}
                                    isProjectVariation={true}
                                    isGlobalUser={userData.global_user}
                                    inProgressQuote={
                                      idx(projectSummary, (_) => _.project.quoteType) === "quote" &&
                                      idx(projectSummary, (_) => _.project.res.status) === "IN_PROGRESS"
                                    }
                                    onRequestVariationFieldOpsToggle={
                                      handleRequestVariationFieldOpsToggle
                                    }
                                  />
                                </DragDropContext>
                              </div>
                            </div>
                          </Paper>
                          {idx(projectSummary, (_) => _.project.projectType) === "PROJECT" ? (
                            <ul
                              className="accordion-icon stage-total-row tabel-list d-flex justify-content-sm-between list-unstyled mb-0"
                              data-cy="revised-project-total-row"
                            >
                              <span className="section-header-spacer" aria-hidden="true" />
                              <li
                                className="first-cell text-start mt-auto mb-auto me-auto tender-task-total"
                                data-cy="revised-project-total"
                              >
                                <span></span>
                                <h6 className="mb-0 ms-2">Revised Project Total</h6>
                              </li>
                              {revisedProjectTotal.completionPercentage && (
                                <>
                                  {financePermission && (
                                    <>
                                      <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px mt-auto" data-cy="material">
                                        <span className="brand-color-1 fw-bold" data-cy={'label'}>Material</span>
                                        <h6 className="mb-0" data-cy="sale-amount">{revisedProjectTotal.materialSaleAmount}</h6>
                                        <span className="profit-percent fst-12 fw-bold">{revisedProjectTotal.materialProfit} ({revisedProjectTotal.materialProfitPercentage})</span>
                                      </li>
                                      <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px mt-auto" data-cy="contractor">
                                        <span className="brand-color-1 fw-bold" data-cy={'label'}>Contractor</span>
                                        <h6 className="mb-0 fw-bold" data-cy="sale-amount">{revisedProjectTotal.subcontractorSaleAmount}</h6>
                                        <span className="profit-percent fst-12 fw-bold">{revisedProjectTotal.subcontractorProfit} ({revisedProjectTotal.subcontractorProfitPercentage})</span>
                                      </li>
                                      <li className="d-flex flex-column flex-start-end justify-content-end align-items-end tender-task-total text-right w-150px mt-auto" data-cy="resource">
                                        <span className="brand-color-1 fw-bold" data-cy={'label'}>Resource</span>
                                        <h6 className="mb-0" data-cy="sale-amount">{revisedProjectTotal.laborSaleAmount}</h6>
                                        <span className="profit-percent fst-12 fw-bold">{revisedProjectTotal.laborProfit} ({revisedProjectTotal.laborProfitPercentage})</span>
                                      </li>
                                    </>
                                  )}

                                  <li className="text-end w-150px mt-auto">
                                    <div data-cy="revised-project-effort " className="d-flex flex-column align-items-end">
                                      <span className="brand-color-1 fw-bold" data-cy={"label"}> Effort</span>
                                      <h6 className="mb-0 fw-bold" data-cy={"value"}>
                                        {revisedProjectTotal.forecastHoursDisplay}
                                      </h6>
                                      <span data-cy={"value"} className="profit-percent fst-12 fw-bold">
                                        {revisedProjectTotal.actualHoursDisplay} ({revisedProjectTotal.effortCompletionPercentage})
                                      </span>
                                    </div>
                                  </li>
                                  <li className="d-flex flex-column flex-start-end align-items-end tender-task-total text-end w-150px mt-auto">
                                    <span className="brand-color-1 fw-bold">Completion %</span>
                                    <h6 className="mb-0 fw-bold">{revisedProjectTotal.completionPercentage}</h6>
                                  </li>
                                </>
                              )}
                              <li
                                data-cy="sales-total-profit"
                                className="d-flex flex-column flex-start-end justify-content-center align-items-end tender-task-total text-end w-180px"
                              >
                                <span className="brand-color-1 fw-bold">Sales</span>
                                <span className="brand-color-1 fw-bold">Profit (%)</span>
                                <h6 className="mb-0 fw-bold">{revisedProjectTotal.totalSaleAmount}</h6>
                                <span className="profit-percent fst-12">
                                  {revisedProjectTotal.totalProfit} (
                                  {revisedProjectTotal.totalProfitPercentage})
                                </span>
                              </li>
                              {/* Placeholder columns to match section header lock/wbs columns for alignment */}
                              {/* <li className="d-flex flex-column justify-content-center align-items-end text-center wbs-lock-label wbs-mapping" /> */}
                              {/* <li className="text-center tender-task-total wbs-mapping" /> */}
                              <li className="d-flex flex-column justify-content-center align-items-end text-center wbs-lock-label wbs-mapping" />
                              {/* <li className="text-center tender-task-total wbs-mapping" />
                            <li className="text-center tender-task-total wbs-mapping" /> */}
                              <li className="action-icons-task pe-3 text-end w-5" />
                            </ul>
                          ) : (
                            ""
                          )}
                        </CardBody>
                      </Card>
                    ) : (
                      ""
                    )}

                    {optionsToProjects.length &&
                      !isConvertedToCurrentProject ? (
                      <Card className="border-0 mt-3">
                        <CardHeader className="sections d-flex justify-content-between align-items-center bg-body">
                          <h6 className="mb-0 text-uppercase">Options</h6>
                        </CardHeader>
                        <CardBody className="d-flex flex-column flex-wrap gap-2 p-0 pt-2">
                          <Paper className="paperContainer viewProjectCol">
                            <div className="projectableContainer">
                              <div
                                className="privewTable"
                                data-cy={"option-table"}
                              >
                                <DragDropContext onDragEnd={onDragEnd}>
                                  <Tasks
                                    options={optionsToProjects}
                                    handleOptionMenu={handleOptionMenu}
                                    handleTaskClick={handleTaskClick}
                                    optionDialogHandler={optionDialogHandler}
                                    switchSection={switchSection}
                                    copyTasksToSection={copyTasksToSection}
                                    deleteDialogHandler={deleteDialogHandler}
                                    projectType={idx(
                                      projectSummary,
                                      (_) => _.project.projectType
                                    )}
                                    financePermission={financePermission}
                                    updatePermission={updatePermission}
                                    writePermission={writePermission}
                                    quoteType={idx(
                                      projectSummary,
                                      (_) => _.project.quoteType
                                    )}
                                    optionsTotal={optionsTotal || {}}
                                    toggleId={taskListState.toggleId}
                                    isProjectOption={true}
                                    isGlobalUser={userData.global_user}
                                  />
                                </DragDropContext>
                              </div>
                            </div>
                          </Paper>
                        </CardBody>
                      </Card>
                    ) : (
                      ""
                    )}
                  </>
                }
              />
            </Routes>
          </Paper>
        </div>
      </div>
      {isConvertedToCurrentProject && (
        <div className="mt-4 pe-0">
          <div className="d-flex gap-2">
            <Button type="submit" className="secondarybtn" data-cy="summary-proposal-button" onClick={() => proposalDialogHandler(ProposalType.SUMMARY)}>
              Summary Proposal
            </Button>
            <Button type="submit" className="secondarybtn" data-cy="detailed-proposal-button" onClick={() => proposalDialogHandler(ProposalType.DETAILED)}>
              Detailed Proposal
            </Button>
          </div>
        </div>
      )}

      {/* Profit Margin Adjustment Modal */}
      <ProfitMarginAdjustment
        open={taskListState.profitRoundingModalOpen}
        onClose={() => setTaskListState(prev => ({ ...prev, profitRoundingModalOpen: false }))}
        projectData={projectSummary}
        projectId={projectId}
        onApplyAdjustment={async (adjustmentData) => {
          try {
            await dispatch(adjustProfitMargin(projectId, adjustmentData));
            // Refresh project summary to show updated values
            await getProjectSummaryData({ projectId, dispatch, type: props.type, forceFresh: true });
            setTaskListState(prev => ({ ...prev, profitRoundingModalOpen: false }));
          } catch (error) {
            console.error('Failed to apply profit margin adjustment:', error);
            // TODO: Show error message to user
          }
        }}
      />
    </div >
  );
}
